//
// Copyright 2024 DXOS.org
//

import WebSocket from 'isomorphic-ws';

import { Mutex, Trigger, scheduleTask, scheduleTaskInterval } from '@dxos/async';
import { Context, Resource } from '@dxos/context';
import { invariant } from '@dxos/invariant';
import { log, logInfo } from '@dxos/log';
import { EdgeWebsocketProtocol } from '@dxos/protocols';
import { buf } from '@dxos/protocols/buf';
import { type Message, MessageSchema } from '@dxos/protocols/buf/dxos/edge/messenger_pb';

import { clientVersionProtocol } from './client-version.ts';
import { protocol } from './defs.ts';
import { type EdgeIdentity } from './edge-identity.ts';
import {
  CLOUDFLARE_MESSAGE_MAX_BYTES,
  EDGE_FLOW_CONTROL,
  MessageTooLargeError,
  WebSocketClosedError,
  WebSocketMuxer,
} from './edge-ws-muxer.ts';
import { toUint8Array } from './protocol.ts';
import { type ReconnectReason, classifyCloseCode, classifySocketError, isOnline } from './reconnect-reason.ts';

const SIGNAL_KEEPALIVE_INTERVAL = 4_000;
const SIGNAL_KEEPALIVE_TIMEOUT = 12_000;
/**
 * Watchdog self-check: if the inactivity timer fires later than its schedule by more than this,
 * the local event loop was starved (heavy WASM sync compute pins it for seconds at a time) —
 * our pings were not being sent and inbound pongs were not being processed, so the silence says
 * nothing about the connection. Probe and re-arm instead of restarting.
 */
const KEEPALIVE_WATCHDOG_LATE_TOLERANCE = 3_000;
/**
 * Probes in a row that may go unanswered before the watchdog restarts even though the loop keeps stalling: a live
 * connection answers between blocks, so only a dead one gets this far.
 */
const KEEPALIVE_MAX_UNANSWERED_PROBES = 3;

/** `WebSocket.CONNECTING`, inlined because `isomorphic-ws` exposes no static in every runtime. */
const WS_CONNECTING = 0;

/**
 * Bound on messages held while the socket completes its handshake. A handshake takes a
 * round trip, so the backlog is small in practice; the cap stops a server that never
 * finishes connecting from growing the queue without limit.
 */
const MAX_PENDING_MESSAGES = 256;

/** A message submitted before the handshake completed, and the trigger its sender awaits. */
type PendingMessage = { message: Message; sent: Trigger };

export type EdgeWsConnectionCallbacks = {
  onConnected: () => void;
  onMessage: (message: Message) => void;
  onRestartRequired: (reason: ReconnectReason) => void;
};

export class EdgeWsConnection extends Resource {
  private _inactivityTimeoutCtx: Context | undefined;
  private _ws: WebSocket | undefined;
  private _wsMuxer: WebSocketMuxer | undefined;
  private _lastReceivedMessageTimestamp = Date.now();

  private _openTimestamp: number | undefined;

  // Latency tracking.
  private _pingTimestamp: number | undefined;
  private _lastPingSentTimestamp = 0;
  /**
   * When a ping last went out later than its interval allows: the event loop was blocked until then,
   * so pongs that arrived meanwhile went unread.
   */
  private _loopStalledAt: number | undefined;
  /** Probes the watchdog sent since anything was last received. */
  private _unansweredProbes = 0;
  private _rtt = 0;

  // Rate tracking with sliding window.
  private _uploadRate = 0;
  private _downloadRate = 0;
  private readonly _rateWindow = 10000; // 10 second sliding window.
  private readonly _rateUpdateInterval = 1000; // Update rates every second.
  private _bytesSamples: Array<{ timestamp: number; sent: number; received: number }> = [];

  private _messagesSent = 0;
  private _messagesReceived = 0;

  /**
   * WebSocket frames arrive in order, but converting frame data to bytes is async
   * (the `Blob` fallback path awaits `blob.arrayBuffer()`), and concurrent conversions
   * are not guaranteed to complete in arrival order. Segmented-message reassembly in
   * `WebSocketMuxer` requires chunks to reach `receiveData` in arrival order, so message
   * processing is serialized through this lock.
   */
  private readonly _receiveMutex = new Mutex();

  /**
   * Messages submitted before the handshake completed. `send()` on a CONNECTING socket throws
   * `InvalidStateError`, and callers holding the connection across a reconnect do exactly that.
   */
  private _pendingMessages: PendingMessage[] = [];

  constructor(
    private readonly _identity: EdgeIdentity,
    private readonly _connectionInfo: { url: URL; protocolHeader?: string; headers?: Record<string, string> },
    private readonly _callbacks: EdgeWsConnectionCallbacks,
  ) {
    super();
  }

  @logInfo
  public get info() {
    return {
      open: this.isOpen,
      identity: this._identity.identityDid,
      device: this._identity.peerKey,
    };
  }

  public get rtt(): number {
    return this._rtt;
  }

  /** Floor uptime to satisfy the int32 EdgeStatus.uptime wire type. */
  public get uptime(): number {
    return this._openTimestamp ? Math.floor((Date.now() - this._openTimestamp) / 1000) : 0;
  }

  public get uploadRate(): number {
    return this._uploadRate;
  }

  public get downloadRate(): number {
    return this._downloadRate;
  }

  public get messagesSent(): number {
    return this._messagesSent;
  }

  public get messagesReceived(): number {
    return this._messagesReceived;
  }

  /**
   * Payload bytes queued locally because the peer has not extended enough credit.
   *
   * Zero on a connection without flow control, where the muxer never holds anything back for credit.
   */
  public get pendingSendBytes(): number {
    return this._wsMuxer?.pendingBytes ?? 0;
  }

  /** @see WebSocketMuxer.unacknowledgedBytes */
  public get unacknowledgedBytes(): number {
    return this._wsMuxer?.unacknowledgedBytes ?? 0;
  }

  /** Whether this connection negotiated credit-based flow control (`edge-ws-v2`). */
  public get flowControlEnabled(): boolean {
    return this._wsMuxer?.flowControlEnabled ?? false;
  }

  /** When EDGE's pause of the service's channel ends, in `Date.now()` time, if EDGE paused it. */
  public pausedUntil(serviceId: string): number | undefined {
    const channelId = this._wsMuxer?.channelFor(serviceId);
    return channelId === undefined ? undefined : this._wsMuxer?.pausedUntil(channelId);
  }

  /**
   * Send, dropping the result.
   *
   * Callers that need the send to reflect flow control must use {@link sendAndWait}: this returns as soon as the
   * message is handed over, which under credit may be long before it reaches the socket.
   */
  public send(message: Message): void {
    void this.sendAndWait(message).catch((error) => {
      // A close mid-send is routine (the close handler reconnects), so it is not reported as an error.
      if (error instanceof WebSocketClosedError) {
        log.verbose('message dropped (websocket closed)', { payload: protocol.getPayloadType(message) });
      } else {
        log.catch(error);
      }
    });
  }

  /**
   * Send, resolving once the last chunk has been handed to the socket.
   *
   * Under flow control that cannot happen until the peer has extended credit for it, so awaiting this is what turns
   * backpressure into something a caller can feel. A message submitted while the socket is still connecting waits for
   * the handshake, and rejects with {@link WebSocketClosedError} if the socket never opens. Rejects with
   * {@link MessageTooLargeError} past the Cloudflare limit, on every protocol.
   */
  public async sendAndWait(message: Message): Promise<void> {
    invariant(this._ws);
    if (this._ws.readyState === WS_CONNECTING) {
      if (this._pendingMessages.length >= MAX_PENDING_MESSAGES) {
        // Drop the oldest: during a reconnect the freshest signalling state is the useful one.
        const dropped = this._pendingMessages.shift();
        log.warn('pending message dropped (queue full while connecting)', {
          payload: dropped && protocol.getPayloadType(dropped.message),
        });
        dropped?.sent.throw(new WebSocketClosedError(WS_CONNECTING));
      }
      const sent = new Trigger();
      this._pendingMessages.push({ message, sent });
      await sent.wait();
      return;
    }
    // The muxer is built on open, so a socket that closed before opening never had one.
    if (!this._wsMuxer) {
      throw new WebSocketClosedError(this._ws.readyState);
    }
    log('sending...', { peerKey: this._identity.peerKey, payload: protocol.getPayloadType(message) });
    this._messagesSent++;
    if (this._ws.protocol?.includes(EdgeWebsocketProtocol.V0)) {
      const binary = buf.toBinary(MessageSchema, message);
      if (binary.length > CLOUDFLARE_MESSAGE_MAX_BYTES) {
        throw new MessageTooLargeError({
          byteLength: binary.byteLength,
          maxByteLength: CLOUDFLARE_MESSAGE_MAX_BYTES,
          serviceId: message.serviceId,
          payload: protocol.getPayloadType(message),
        });
      }
      this._recordBytes(binary.byteLength, 0);
      this._ws.send(binary);
    } else {
      // For muxer, we need to track the size of the message being sent.
      const binary = buf.toBinary(MessageSchema, message);
      this._recordBytes(binary.byteLength, 0);
      await this._wsMuxer.send(message);
    }
  }

  protected override async _open(): Promise<void> {
    const baseProtocols = [...Object.values(EdgeWebsocketProtocol), clientVersionProtocol()];
    const ws = new WebSocket(
      this._connectionInfo.url.toString(),
      this._connectionInfo.protocolHeader
        ? [...baseProtocols, this._connectionInfo.protocolHeader]
        : [...baseProtocols],
      this._connectionInfo.headers ? { headers: this._connectionInfo.headers } : undefined,
    );
    this._ws = ws;
    // Deliver frame data as `ArrayBuffer` rather than `Blob` so bytes are available
    // synchronously; avoids the async `blob.arrayBuffer()` reads that can otherwise
    // complete out of arrival order (see `_receiveChain`).
    ws.binaryType = 'arraybuffer';
    // Built on open rather than here: flow control is negotiated via the subprotocol, which is not known until the
    // handshake completes, and messages sent before then wait in `_pendingMessages`. The handlers below close over this
    // binding rather than reading `this._wsMuxer`, so a late event from this socket cannot act on the next one's muxer.
    let muxer: WebSocketMuxer | undefined;

    ws.onopen = () => {
      muxer = new WebSocketMuxer(ws, {
        flowControl: ws.protocol?.includes(EdgeWebsocketProtocol.V2) ? EDGE_FLOW_CONTROL : undefined,
        // EDGE is the trusted end, so its overdraft is only logged: closing on its account costs more than the overrun.
        onOverdraft: (overdraft) => log.warn('edge exceeded its flow-control window', overdraft),
      });
      this._wsMuxer = muxer;
      if (this.isOpen) {
        log('connected');
        this._openTimestamp = Date.now();
        this._flushPendingMessages();
        this._callbacks.onConnected();
        this._scheduleHeartbeats();
        this._scheduleRateCalculation();
      } else {
        this._dropPendingMessages(ws.readyState);
        log.verbose('connected after becoming inactive', { currentIdentity: this._identity });
      }
    };
    ws.onclose = (event: WebSocket.CloseEvent) => {
      if (this.isOpen) {
        const reason = classifyCloseCode(event.code, isOnline());
        log.warn('server disconnected', { code: event.code, reason: event.reason, classified: reason });
        this._dropPendingMessages(ws.readyState);
        this._callbacks.onRestartRequired(reason);
        muxer?.destroy();
      }
    };
    ws.onerror = (event: WebSocket.ErrorEvent) => {
      if (this.isOpen) {
        log.warn('edge connection socket error', { error: event.error, info: event.message });
        this._callbacks.onRestartRequired(classifySocketError(isOnline()));
      } else {
        log.verbose('error ignored on closed connection', { error: event.error });
      }
    };
    /**
     * https://developer.mozilla.org/en-US/docs/Web/API/MessageEvent/data
     */
    ws.onmessage = (event: WebSocket.MessageEvent) => {
      if (!this.isOpen) {
        log.verbose('message ignored on closed connection', { event: event.type });
        return;
      }
      this._lastReceivedMessageTimestamp = Date.now();
      this._unansweredProbes = 0;
      if (event.data === '__pong__') {
        // Calculate latency.
        if (this._pingTimestamp) {
          this._rtt = Date.now() - this._pingTimestamp;
          this._pingTimestamp = undefined;
        }
        this._rescheduleHeartbeatTimeout();
        return;
      }

      if (!muxer) {
        log.verbose('message ignored before open', { event: event.type });
        return;
      }
      // `_receiveMessage` serializes on `_receiveMutex`; `acquire` enqueues synchronously,
      // so locks are taken in arrival order regardless of async conversion timing.
      void this._receiveMessage(event.data, muxer).catch((err) => log.catch(err));
    };
  }

  /** Replays messages buffered during the handshake, in submission order. */
  private _flushPendingMessages(): void {
    const pending = this._pendingMessages;
    this._pendingMessages = [];
    for (const { message, sent } of pending) {
      this.sendAndWait(message).then(
        () => sent.wake(),
        (error) => sent.throw(error),
      );
    }
  }

  /** Fails every message buffered during a handshake that will not complete. */
  private _dropPendingMessages(readyState: number): void {
    const pending = this._pendingMessages;
    this._pendingMessages = [];
    for (const { sent } of pending) {
      sent.throw(new WebSocketClosedError(readyState));
    }
  }

  private async _receiveMessage(data: WebSocket.Data, muxer: WebSocketMuxer): Promise<void> {
    // Serialize processing so bytes reach `muxer.receiveData` in arrival order. The guard
    // releases on scope exit even if processing throws, so a single bad message is logged
    // and dropped instead of stalling every message queued after it.
    using _guard = await this._receiveMutex.acquire();
    const bytes = await toUint8Array(data);
    this._recordBytes(0, bytes.byteLength);
    if (!this.isOpen) {
      return;
    }

    this._messagesReceived++;

    if (this._ws?.protocol?.includes(EdgeWebsocketProtocol.V0)) {
      const message = buf.fromBinary(MessageSchema, bytes);
      log('received', { from: message.source, payload: protocol.getPayloadType(message) });
      this._callbacks.onMessage(message);
      return;
    }

    const frame = muxer.receiveFrame(bytes);
    try {
      if (frame.message) {
        log('received', { from: frame.message.source, payload: protocol.getPayloadType(frame.message) });
        this._callbacks.onMessage(frame.message);
      }
    } finally {
      // Credited after dispatch, which for a synchronous listener fan-out is the best signal available; a listener
      // that throws has still finished with the frame.
      muxer.consumed(frame);
    }
  }

  protected override async _close(): Promise<void> {
    void this._inactivityTimeoutCtx?.dispose().catch(() => {});
    this._dropPendingMessages(this._ws?.readyState ?? WS_CONNECTING);

    try {
      this._ws?.close();
      this._ws = undefined;
      this._wsMuxer?.destroy();
      this._wsMuxer = undefined;
    } catch (err) {
      if (err instanceof Error && err.message.includes('WebSocket is closed before the connection is established.')) {
        return;
      }
      log.warn('error closing websocket', { err });
    }
  }

  private _scheduleHeartbeats(): void {
    invariant(this._ws);
    scheduleTaskInterval(
      this._ctx,
      async () => {
        // TODO(mykola): use RFC6455 ping/pong once implemented in the browser?
        // Cloudflare's worker responds to this `without interrupting hibernation`. https://developers.cloudflare.com/durable-objects/api/websockets/#setwebsocketautoresponse
        this._sendPing();
      },
      SIGNAL_KEEPALIVE_INTERVAL,
    );
    this._sendPing();
    this._rescheduleHeartbeatTimeout();
  }

  private _sendPing(): void {
    if (!this._ws) {
      return;
    }
    const now = Date.now();
    if (
      this._lastPingSentTimestamp &&
      now - this._lastPingSentTimestamp > SIGNAL_KEEPALIVE_INTERVAL + KEEPALIVE_WATCHDOG_LATE_TOLERANCE
    ) {
      this._loopStalledAt = now;
    }
    this._pingTimestamp = now;
    this._lastPingSentTimestamp = now;
    this._ws.send('__ping__');
  }

  /**
   * Inactivity watchdog. Restarts the connection only after a fair trial: pings were actually
   * flowing (a recent send), the timer fired on schedule and no ping went out late within the
   * window (the local event loop was alive to process an answer), and still nothing was received
   * for the full window. Wall-clock silence alone is not evidence — sync compute can pin the event
   * loop for seconds, during which the ping sender does not run and arrived pongs are not
   * processed; restarting a healthy connection on that basis costs a re-handshake and fails
   * in-flight sync rounds. A stalled loop is excused only until {@link KEEPALIVE_MAX_UNANSWERED_PROBES}
   * probes in a row go unanswered.
   */
  private _rescheduleHeartbeatTimeout(): void {
    if (!this.isOpen) {
      return;
    }
    void this._inactivityTimeoutCtx?.dispose();
    this._inactivityTimeoutCtx = new Context();
    const armedAt = Date.now();
    scheduleTask(
      this._inactivityTimeoutCtx,
      () => {
        if (!this.isOpen) {
          return;
        }
        const now = Date.now();
        const silenceMs = now - this._lastReceivedMessageTimestamp;
        if (silenceMs <= SIGNAL_KEEPALIVE_TIMEOUT) {
          this._rescheduleHeartbeatTimeout();
          return;
        }
        const pingAgeMs = this._lastPingSentTimestamp ? now - this._lastPingSentTimestamp : Number.POSITIVE_INFINITY;
        const firedLateByMs = now - armedAt - SIGNAL_KEEPALIVE_TIMEOUT;
        const pingsWereFlowing = pingAgeMs <= SIGNAL_KEEPALIVE_INTERVAL * 2;
        // The timer fires on time when the loop was blocked for most of the window but freed up before
        // the deadline, so the late ping that block caused has to count too.
        const loopStalledAgoMs =
          this._loopStalledAt === undefined ? Number.POSITIVE_INFINITY : now - this._loopStalledAt;
        const loopWasLive =
          firedLateByMs < KEEPALIVE_WATCHDOG_LATE_TOLERANCE && loopStalledAgoMs >= SIGNAL_KEEPALIVE_TIMEOUT;
        if ((pingsWereFlowing && loopWasLive) || this._unansweredProbes >= KEEPALIVE_MAX_UNANSWERED_PROBES) {
          log.warn('restart due to inactivity timeout', {
            silenceMs,
            pingAgeMs,
            unansweredProbes: this._unansweredProbes,
            lastReceivedMessageTimestamp: this._lastReceivedMessageTimestamp,
          });
          this._callbacks.onRestartRequired('inactivity_timeout');
          return;
        }
        // The silence is self-inflicted (starved event loop stopped our pings and delayed this
        // timer). Probe immediately and give the connection a fresh full window to answer.
        log.verbose('keepalive starved by event loop; probing instead of restarting', {
          silenceMs,
          pingAgeMs,
          firedLateByMs,
          loopStalledAgoMs,
          unansweredProbes: this._unansweredProbes,
        });
        this._unansweredProbes++;
        this._sendPing();
        this._rescheduleHeartbeatTimeout();
      },
      SIGNAL_KEEPALIVE_TIMEOUT,
    );
  }

  private _recordBytes(sent: number, received: number): void {
    const now = Date.now();

    // Find if we have a sample for the current second.
    const currentSecond = Math.floor(now / 1000) * 1000;
    const existingSample = this._bytesSamples.find((s) => Math.floor(s.timestamp / 1000) * 1000 === currentSecond);

    if (existingSample) {
      existingSample.sent += sent;
      existingSample.received += received;
    } else {
      this._bytesSamples.push({ timestamp: now, sent, received });
    }
  }

  private _scheduleRateCalculation(): void {
    scheduleTaskInterval(
      this._ctx,
      async () => {
        this._calculateRates();
      },
      this._rateUpdateInterval,
    );
    // Calculate initial rates.
    this._calculateRates();
  }

  private _calculateRates(): void {
    const now = Date.now();
    const cutoff = now - this._rateWindow;

    // Remove old samples.
    this._bytesSamples = this._bytesSamples.filter((s) => s.timestamp > cutoff);

    if (this._bytesSamples.length === 0) {
      this._uploadRate = 0;
      this._downloadRate = 0;
      return;
    }

    // Calculate total bytes and time span.
    let totalSent = 0;
    let totalReceived = 0;
    const oldestTimestamp = Math.min(...this._bytesSamples.map((s) => s.timestamp));
    const timeSpan = (now - oldestTimestamp) / 1000; // Convert to seconds.

    for (const sample of this._bytesSamples) {
      totalSent += sample.sent;
      totalReceived += sample.received;
    }

    // Calculate rates (bytes per second).
    this._uploadRate = timeSpan > 0 ? Math.round(totalSent / timeSpan) : 0;
    this._downloadRate = timeSpan > 0 ? Math.round(totalReceived / timeSpan) : 0;
  }
}
